import React, { useCallback, useEffect, useRef } from "react";
import { Box, Grommet, Heading, Nav, Text } from "grommet";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { funderAllocationTheme } from "./funderAllocationTheme";
import {
    useAuth,
    DirtyGuardProvider,
    useDirtyGuard,
    ScrollContext,
} from "../context";
import { navItemStyle } from "../sections/funder-allocation/tabStyle";
import { Panel } from "../sections/funder-allocation/Panel";

const FunderAllocationNav = () => {
    const { logout } = useAuth();
    const { confirmLeave } = useDirtyGuard();

    const guardedClick = (e) => {
        if (!confirmLeave()) e.preventDefault();
    };

    return (
        <Panel>
            <Box direction="row" justify="between" align="center">
                <Heading level="2" margin="none">
                    Funder Allocation
                </Heading>
                <Text
                    size="small"
                    onClick={logout}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                >
                    Log out
                </Text>
            </Box>
            <Nav direction="row" gap="medium">
                <NavLink
                    to="loans"
                    style={({ isActive }) => navItemStyle(isActive)}
                    onClick={guardedClick}
                >
                    Loans
                </NavLink>
                <NavLink
                    to="funders"
                    style={({ isActive }) => navItemStyle(isActive)}
                    onClick={guardedClick}
                >
                    Funders
                </NavLink>
                <NavLink
                    to="audit"
                    style={({ isActive }) => navItemStyle(isActive)}
                    onClick={guardedClick}
                >
                    Audit Log
                </NavLink>
            </Nav>
        </Panel>
    );
};

const FunderAllocation = () => {
    const location = useLocation();
    const containerRef = useRef(null);

    // <Grommet full> renders its own height:100vh/overflow:auto root div -
    // that's the real scroll container, not the window. react-router
    // doesn't reset scroll position on navigation, so landing on a page
    // taller than the one you came from (the audit log especially) left
    // you scrolled past the top padding, looking like it had vanished.
    const scrollToTop = useCallback(() => {
        containerRef.current?.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        scrollToTop();
    }, [location.pathname, scrollToTop]);

    // overflow:auto only reserves scrollbar width once content is tall
    // enough to need it - switching between a short page (no scrollbar)
    // and a tall one (scrollbar appears) shifts the centred content
    // sideways by the scrollbar's width. Forcing the track to always be
    // there removes the shift. Set directly on the DOM node rather than a
    // prop, since <Grommet full>'s overflow:auto comes from its own
    // generated class, which an inline style here correctly overrides.
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.overflowY = "scroll";
        }
    }, []);

    return (
        <ScrollContext.Provider value={scrollToTop}>
            <Grommet ref={containerRef} theme={funderAllocationTheme} full>
                <DirtyGuardProvider>
                    <Box align="center">
                        <Box
                            width={{ max: "65%", width: "100%" }}
                            pad={{
                                horizontal: "large",
                                top: "large",
                                bottom: "large",
                            }}
                            gap="medium"
                        >
                            <FunderAllocationNav />
                            <Box>
                                <Outlet />
                            </Box>
                        </Box>
                    </Box>
                </DirtyGuardProvider>
            </Grommet>
        </ScrollContext.Provider>
    );
};

export { FunderAllocation };
